import "./SearchScreen.css"
import React, { useEffect, useState, useRef, useCallback } from "react"
import requests from "../api/requests"
import { useNavigate, useParams } from "react-router-dom"
import Nav from "../components/Nav"
import CosmosBackground from "../components/CosmosBackground"
import LockIcon from "@mui/icons-material/Lock"
import { Skeleton } from "@mui/material"

const baseURL = process.env.REACT_APP_CMS_URL

function SearchCard({ content, index }) {
  const [loaded, setLoaded] = useState(false)
  const navigate = useNavigate()

  const isSerie = content?.content_type === "serie"
  const isDraft = content?.status === "draft"
  const progress = content?.watched_percentage || 0

  const handleClick = () => {
    if (isDraft) {
      navigate("/draft", { state: { title: content?.title, publish_at: content?.publish_at } })
      return
    }
    if (isSerie) {
      navigate(`/series/${content?.id}`)
      return
    }
    navigate(`/episodes/${content?.id}`)
  }

  const draftDate = isDraft && content?.publish_at
    ? new Date(content.publish_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    : null

  return (
    <div
      className="searchCard"
      style={{ animationDelay: `${index * 30}ms` }}
      onClick={handleClick}
    >
      <span className={`searchCard__badge ${isSerie ? "searchCard__badge--serie" : "searchCard__badge--episode"}`}>
        {isSerie ? "Série" : "Episódio"}
      </span>

      <img
        loading="lazy"
        src={encodeURI(`${baseURL}${content?.thumbnail?.sizes?.poster?.url}`)}
        alt={content?.thumbnail?.alt || content?.title || ""}
        className={`searchCard__poster${loaded ? " searchCard__poster--loaded" : ""}`}
        onLoad={() => setLoaded(true)}
        width={250}
        height={375}
      />

      <div className="searchCard__overlay">
        <p className="searchCard__overlay-title">{content?.title}</p>
      </div>

      {isDraft && (
        <div className="searchCard__draftOverlay">
          <LockIcon style={{ fontSize: "1.5rem" }} />
          {draftDate && <span>Disponível em {draftDate}</span>}
        </div>
      )}

      {loaded && progress > 0 && (
        <div className="searchCard__progress" style={{ width: `${progress * 100}%` }} />
      )}
    </div>
  )
}

function SearchScreen() {
  const { title } = useParams()
  const navigate = useNavigate()

  const [contents, setContents] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [page, setPage] = useState(1)
  const observerRef = useRef(null)

  useEffect(() => {
    setContents([])
    setPage(1)
  }, [title])

  const fetchData = useCallback(
    async (currentPage) => {
      const response = await requests.search(currentPage, 20, title)
      if (!response.data) return
      setHasNextPage(response.data?.hasNextPage)
      setContents((prev) => [...prev, ...response.data?.contents])
    },
    [title],
  )

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true)
        await fetchData(1)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    loadInitialData()
  }, [fetchData])

  useEffect(() => {
    if (!hasNextPage || loading) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setPage((p) => p + 1)
      },
      { threshold: 1.0 },
    )

    if (observerRef.current) observer.observe(observerRef.current)
    const currentRef = observerRef.current
    return () => { if (currentRef) observer.unobserve(currentRef) }
  }, [hasNextPage, loading])

  useEffect(() => {
    if (page > 1) fetchData(page)
  }, [page, fetchData])

  return (
    <div className="searchScreen">
      <CosmosBackground />
      <Nav />
      <div className="searchScreen__body">
        {!loading && (
          <h2 className="searchScreen__heading">
            {contents.length > 0
              ? `Resultados para "${title}"`
              : null}
          </h2>
        )}

        {loading ? (
          <div className="searchScreen__grid">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width={250}
                height={375}
                animation="wave"
                style={{ borderRadius: "10px", flexShrink: 0 }}
              />
            ))}
          </div>
        ) : contents.length === 0 ? (
          <div className="searchScreen__empty">
            <p className="searchScreen__empty-icon">🔍</p>
            <h1>Nenhum resultado para "{title}"</h1>
            <p>Tente buscar por outro termo.</p>
            <button className="searchScreen__back" onClick={() => navigate("/")}>
              Voltar ao início
            </button>
          </div>
        ) : (
          <div className="searchScreen__grid">
            {contents.map((content, i) => (
              <SearchCard key={content?.id || i} content={content} index={i} />
            ))}
          </div>
        )}

        {hasNextPage && !loading && (
          <div ref={observerRef} className="searchScreen__sentinel" />
        )}
      </div>
    </div>
  )
}

export default SearchScreen
